import DraftRecoveryChoices from "../Components/DraftRecoveryChoices";
import useRecoverableAutosave from "../hooks/useRecoverableAutosave";
import DraftSaveStatus from "../Components/DraftSaveStatus";
import Page from "../Components/Page";
import Stepper from "../Components/Stepper";

import RotationForm from "../Components/CropYearForms/RotationForm";
import FieldOperationsForm from "../Components/CropYearForms/FieldOperationsForm";
import HarvestForm from "../Components/CropYearForms/HarvestForm";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../Components/Auth/useAuth";
import {
  buildLegacyHeaders,
  fetchCropYear,
  fetchFarm,
  fetchField,
  fetchFourRAssessment,
  axiosInstance,
  normalizeCropYearShape,
} from "../Components/axiosFetchers";
import CropYearCompletionIndicator from "../Components/CropYearCompletionIndicator.jsx";
import VersionHistoryControl, {
  HistoryViewingBanner,
} from "../Components/VersionHistoryControl";
import {
  CROPYEAR_CREATOR,
  FOUR_R_ASSESSMENT_CREATOR,
} from "../Assets/contextFactories_V0";
import useVersionHistory from "../hooks/useVersionHistory";
import {
  getCropYearRouteStep,
  getCropYearStepIndex,
  getCropYearStepKey,
  getCropYearStepPath,
} from "../utils/cropYearRoutes.js";
import {
  buildCropYearPayload,
  buildFourRAssessmentPayload,
} from "../Components/Stepper.jsx";
import { hasComparableFormChanges } from "../utils/formComparison";
import {
  ResourceBreadcrumb,
  buildCropYearBreadcrumb,
} from "../Components/ResourceBreadcrumb.jsx";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  DATA_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";
import { Alert, Button } from "@mui/material";
import { formDraftKey, readFormDraftAsync, clearFormDraft } from "../utils/formDrafts";
import { buildFieldContextQuickFacts } from "../utils/resourceQuickFacts.js";

const steps = [
  { label: "Rotation", formComponent: RotationForm },
  { label: "Field Operations", formComponent: FieldOperationsForm },
  { label: "Harvest", formComponent: HarvestForm },
];

function cloneFormSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return snapshot;
  }

  return JSON.parse(JSON.stringify(snapshot));
}

export default function CropYearPage({ requireSelectedGrower = false }) {
  const [farm, setFarm] = useState(null);
  const [field, setField] = useState(null);
  const [cropYear, setCropYear] = useState(CROPYEAR_CREATOR());
  const [savedCropYear, setSavedCropYear] = useState(CROPYEAR_CREATOR());
  const [fourRAssessment, setFourRAssessment] = useState(
    FOUR_R_ASSESSMENT_CREATOR()
  );
  const [savedFourRAssessment, setSavedFourRAssessment] = useState(
    FOUR_R_ASSESSMENT_CREATOR()
  );
  const [fourRState, setFourRState] = useState({
    has4RAssessment: false,
    completed4R: false,
  });
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [draftError, setDraftError] = useState("");
  const { farmId, fieldId, cropyearId } = useParams();
  const { user, impersonatedEmail } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const [loadedDraftKey, setLoadedDraftKey] = useState("");
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canWriteSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_WRITE_SCOPE);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const location = useLocation();
  const currentStepKey = getCropYearRouteStep(location.pathname);
  const activeStep = getCropYearStepIndex(currentStepKey);
  const isEditing = Boolean(cropyearId);
  const cropYearDraftKey = formDraftKey({ user, impersonatedEmail, growerPublicId: selectedAggregatorGrowerPublicId }, `cropyear:${farmId}:${fieldId}:${cropyearId || "new"}`);
  const updateCropYear = setCropYear;
  const updateFourRAssessment = setFourRAssessment;
  const updateFourRState = setFourRState;

  const history = useVersionHistory({
    user,
    enabled: Boolean(cropyearId),
    historyPath:
      farmId && fieldId && cropyearId
        ? `/farms/${farmId}/fields/${fieldId}/cropyears/${cropyearId}`
        : "",
    currentData: cropYear,
    normalizeDetail: normalizeCropYearShape,
    onRestore: async (snapshot) => {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/${cropyearId}`,
        buildCropYearPayload(snapshot),
        {
          headers: buildLegacyHeaders(user),
        }
      );

      return normalizeCropYearShape(response.data);
    },
    onRestoreSuccess: (savedCropYear) => {
      setCropYear(savedCropYear);
      setSavedCropYear(cloneFormSnapshot(savedCropYear));
      clearFormDraft(cropYearDraftKey);
    },
  });
  const displayedCropYear = {
    ...(history.versionData || cropYear),
    // Capabilities describe the current service, even for cached drafts/history.
    holos_supported_crops: savedCropYear.holos_supported_crops,
  };
  const isViewingHistoricalVersion = history.isViewingHistoricalVersion;

  document.title = `${isEditing ? "Edit" : "Add"} Crop Year Page - Field To Market Canada`;

  useEffect(() => {
    if (
      requireSelectedGrower &&
      (!selectedAggregatorGrowerPublicId ||
        !canReadSelectedGrower ||
        (!cropyearId && !canWriteSelectedGrower))
    ) {
      setDraftError("");
      setIsCreatingDraft(false);
      return undefined;
    }

    let isMounted = true;
    setLoadedDraftKey("");
    setDraftError("");

    async function startup() {
      try {
        if (!cropyearId) {
          setDraftError("");
          setIsCreatingDraft(true);

          const [nextFarm, nextField] = await Promise.all([
            fetchFarm(farmId, userRef.current),
            fetchField(farmId, fieldId, userRef.current),
          ]);

          if (!isMounted) {
            return;
          }

          setFarm(nextFarm);
          setField(nextField);

          const response = await axiosInstance.post(
            `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears`,
            {},
            {
              headers: buildLegacyHeaders(userRef.current),
            }
          );

          if (!isMounted) {
            return;
          }

          navigateRef.current(
            getCropYearStepPath(
              {
                farmId,
                fieldId,
                cropyearId: response.data.id,
              },
              "rotation"
            ),
            { replace: true }
          );
          return;
        }

        setIsCreatingDraft(false);

        const [nextFarm, nextField, nextCropYear] = await Promise.all([
          fetchFarm(farmId, userRef.current),
          fetchField(farmId, fieldId, userRef.current),
          fetchCropYear(farmId, fieldId, cropyearId, userRef.current),
        ]);

        const nextFourRAssessment = nextCropYear?.has_assessment
          ? await fetchFourRAssessment(cropyearId, userRef.current)
          : null;

        if (!isMounted) {
          return;
        }

        setFarm(nextFarm);
        setField(nextField);
        setCropYear(nextCropYear);
        setSavedCropYear(cloneFormSnapshot(nextCropYear));
        setFourRAssessment(nextFourRAssessment || FOUR_R_ASSESSMENT_CREATOR());
        setSavedFourRAssessment(cloneFormSnapshot(nextFourRAssessment || FOUR_R_ASSESSMENT_CREATOR()));
        setFourRState({ has4RAssessment: Boolean(nextCropYear?.has_assessment), completed4R: Boolean(nextCropYear?.has_assessment) });
        const localRecovery = await readFormDraftAsync(cropYearDraftKey);
      if (!isMounted) return;
      setRecoverableDraft(localRecovery);
        setLoadedDraftKey(cropYearDraftKey);

      } catch (error) {
        console.error(error);

        if (!isMounted) {
          return;
        }

        setDraftError(cropyearId ? "We couldn't load this crop year. Reload to try again." : "We couldn't create a draft crop year right now.");
        setIsCreatingDraft(false);
      }
    }

    startup();

    return () => {
      isMounted = false;
    };
  }, [
    cropYearDraftKey,
    cropyearId,
    farmId,
    fieldId,
    canReadSelectedGrower,
    canWriteSelectedGrower,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
  ]);

  function handleStepChange(nextStepIndex) {
    if (!cropyearId) {
      return;
    }

    navigate(
      getCropYearStepPath(
        {
          farmId,
          fieldId,
          cropyearId,
        },
        getCropYearStepKey(nextStepIndex)
      )
    );
  }

  const shouldCompareAssessmentChanges =
    fourRState.has4RAssessment;
  const hasAssessmentSelectionChange =
    Boolean(fourRState.has4RAssessment) !==
    Boolean(savedCropYear?.has_assessment);
  const hasUnsavedChanges =
    hasComparableFormChanges(
      buildCropYearPayload(cropYear),
      buildCropYearPayload(savedCropYear)
    ) ||
    hasAssessmentSelectionChange ||
    (shouldCompareAssessmentChanges &&
      hasComparableFormChanges(
        buildFourRAssessmentPayload(fourRAssessment),
        buildFourRAssessmentPayload(savedFourRAssessment)
      ));

  useEffect(() => {
    if (!cropyearId || loadedDraftKey !== cropYearDraftKey || recoverableDraft || isViewingHistoricalVersion || !canWriteSelectedGrower) return;
    const beforeUnload = (event) => { if (hasUnsavedChanges) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [cropyearId, loadedDraftKey, cropYearDraftKey, recoverableDraft, isViewingHistoricalVersion, canWriteSelectedGrower, hasUnsavedChanges, cropYear, fourRAssessment, fourRState, savedCropYear.current_version_id]);

  const autosave = useRecoverableAutosave({ draftKey: cropYearDraftKey,
    value: { cropYear, fourRAssessment, fourRState },
    dirty: hasUnsavedChanges || hasComparableFormChanges(cropYear, savedCropYear) || hasComparableFormChanges(fourRAssessment, savedFourRAssessment),
    versionId: savedCropYear.current_version_id,
    target: { kind: "cropyear", farm_id: Number(farmId), field_id: Number(fieldId), resource_id: cropyearId ? Number(cropyearId) : null, base_assessment: savedCropYear.has_assessment ? buildFourRAssessmentPayload(savedFourRAssessment) : null },
    enabled: Boolean(cropyearId) && loadedDraftKey === cropYearDraftKey && !isViewingHistoricalVersion && canWriteSelectedGrower, suspendSync: Boolean(recoverableDraft),
    onRecover: setRecoverableDraft });

  if (requireSelectedGrower && !selectedAggregatorGrowerPublicId) {
    return (
      <Page
        showQuickFacts={false}
        title={"Crop Year"}
        headerBorderColor={"border-[#34a853]"}
        headerVariant="hybrid"
        contentWidth="wide"
        quickFactsVariant="inline-card"
        padding="px-4 py-5 md:px-6 md:py-7"
        introContent={
          <p>
            Select an authorized grower before opening crop-year data entry.
          </p>
        }
      >
        <AggregatorSelectedGrowerPanel selectedGrower={selectedAggregatorGrower} />
      </Page>
    );
  }

  if (requireSelectedGrower && !canReadSelectedGrower) {
    return (
      <Page
        showQuickFacts={false}
        title={"Crop Year"}
        headerBorderColor={"border-[#34a853]"}
        headerVariant="hybrid"
        contentWidth="wide"
        quickFactsVariant="inline-card"
        padding="px-4 py-5 md:px-6 md:py-7"
        introContent={<p>This delegation cannot view crop-year data.</p>}
      >
        <div className="space-y-5">
          <AggregatorSelectedGrowerPanel selectedGrower={selectedAggregatorGrower} />
          <AggregatorScopeWarning
            message="This selected grower link cannot view crop-year records."
            requiredScope={DATA_READ_SCOPE}
          />
        </div>
      </Page>
    );
  }

  if (requireSelectedGrower && !cropyearId && !canWriteSelectedGrower) {
    return (
      <Page
        showQuickFacts={false}
        title={"New Crop Year"}
        headerBorderColor={"border-[#34a853]"}
        headerVariant="hybrid"
        contentWidth="wide"
        quickFactsVariant="inline-card"
        padding="px-4 py-5 md:px-6 md:py-7"
        introContent={<p>This delegation cannot create crop-year drafts.</p>}
      >
        <div className="space-y-5">
          <AggregatorSelectedGrowerPanel selectedGrower={selectedAggregatorGrower} />
          <AggregatorScopeWarning
            message="This selected grower link cannot create or update grower records."
            requiredScope={DATA_WRITE_SCOPE}
          />
        </div>
      </Page>
    );
  }

  if (!cropyearId) {
    return (
      <Page
        showQuickFacts={false}
        title={"New Crop Year"}
        headerBorderColor={"border-[#34a853]"}
        headerVariant="hybrid"
        contentWidth="wide"
        quickFactsVariant="inline-card"
        padding="p-[0px]"
        introContent={
          <p>
            A draft crop year is created first so your progress can be saved as you
            move through rotation, field operations, and harvest.
          </p>
        }
      >
        <div className="h-full w-full ">
        <DraftSaveStatus state={autosave} />
          {requireSelectedGrower ? (
            <div className="px-4 pt-5 md:px-6">
              <AggregatorSelectedGrowerPanel
                selectedGrower={selectedAggregatorGrower}
              />
            </div>
          ) : null}
          {isCreatingDraft ? (
            <div className="px-6 py-10 text-[#666666]">
              Creating a draft crop year so your progress can be saved as you go.
            </div>
          ) : (
            <div className="px-6 py-10 text-[#666666]">
              <p>{draftError}</p>
            </div>
          )}
        </div>
      </Page>
    );
  }

  if (loadedDraftKey !== cropYearDraftKey || !farm || !field || !cropYear?.id) {
    return (
      <Page
        showQuickFacts={false}
        title={"Edit Crop Year"}
        headerBorderColor={"border-[#34a853]"}
        headerVariant="hybrid"
        contentWidth="wide"
        quickFactsVariant="inline-card"
        padding="p-[0px]"
        introContent={
          <p>
            Loading this crop-year draft and its field context before editing.
          </p>
        }
      >
        <div className="px-6 py-10 text-[#666666]">
          {draftError || "Loading crop year..."}
          {draftError ? <Button onClick={() => window.location.reload()}>Reload crop year</Button> : null}
        </div>
      </Page>
    );
  }

  return (
    <Page
      showQuickFacts={true}
      title={
        displayedCropYear?.crop_year
          ? `${displayedCropYear.crop_year} ${String(
              displayedCropYear.crop_this_year || "Crop Year"
            ).replaceAll("_", " ")}`
          : "New Crop Year"
      }
      headerBorderColor={"border-[#34a853]"}
      headerVariant="hybrid"
      contentWidth="wide"
      quickFactsVariant="inline-card"
      headerActions={
        cropyearId ? (
          <VersionHistoryControl
            activeSummary={history.activeSummary}
            canViewNewer={history.canViewNewer}
            canViewOlder={history.canViewOlder}
            detailState={history.detailState}
            dialogOpen={history.dialogOpen}
            errorMessage={history.errorMessage}
            onClose={history.closeDialog}
            onOpen={history.openDialog}
            onRestore={history.restoreSelectedVersion}
            onSelectVersion={history.selectVersion}
            onShowCurrent={history.showCurrentVersion}
            onViewNewer={
              history.canViewNewer
                ? () => history.selectAdjacentVersion(-1)
                : undefined
            }
            onViewOlder={
              history.canViewOlder
                ? () => history.selectAdjacentVersion(1)
                : undefined
            }
            resourceLabel="Crop Year"
            restoreState={history.restoreState}
            summaries={history.summaries}
            summaryState={history.summaryState}
            summaryTitle={(summary) =>
              summary?.crop_year || summary?.crop_this_year
                ? `${summary.crop_year || "Draft"} ${summary.crop_this_year || ""}`.trim()
                : `Crop Year v${summary?.version_no || "-"}`
            }
          />
        ) : null
      }
      introContent={
        farm?.name || field?.name ? (
          <p>{[farm?.name, field?.name].filter(Boolean).join(" / ")}</p>
        ) : null
      }
      padding="p-[0px]"
      quickFactsData={[
        ...buildFieldContextQuickFacts({
          farm,
          field,
          auditRecord: displayedCropYear,
        }),
        { label: "Crop", value: displayedCropYear?.crop_this_year || null },
        { label: "Crop Year", value: displayedCropYear?.crop_year || null },
        {
          label: "Completion",
          value: `${
            Number.isFinite(Number(displayedCropYear?.completion_percent))
              ? Number(displayedCropYear?.completion_percent)
              : 0
          }%`,
        },
      ]}
    >
      <div className="h-full w-full ">
        <DraftSaveStatus state={autosave} />
        {recoverableDraft && canWriteSelectedGrower ? <Alert severity="info" className="m-4" action={<div className="flex gap-2">
          <Button onClick={() => {
            autosave.selectRecovery(recoverableDraft);
            setCropYear({ ...recoverableDraft.value.cropYear, current_version_id: savedCropYear.current_version_id });
            setFourRAssessment(recoverableDraft.value.fourRAssessment || FOUR_R_ASSESSMENT_CREATOR());
            setFourRState(recoverableDraft.value.fourRState || { has4RAssessment: false, completed4R: false });
            setRecoverableDraft(null);
          }}>Restore crop year draft</Button>
          <Button onClick={() => { autosave.discard(recoverableDraft); setRecoverableDraft(null); }}>Discard crop year draft</Button>
        </div>}>
          Recoverable crop-year edits are available. Saving applies them to the crop year.
          <DraftRecoveryChoices draft={recoverableDraft} onChoose={setRecoverableDraft} />
          {recoverableDraft.versionId !== savedCropYear.current_version_id ? " The saved crop year has changed; review the restored values before saving." : ""}
        </Alert> : null}
        <div className="px-4 pt-5 md:px-6">
          {requireSelectedGrower ? (
            <div className="mb-5">
              <AggregatorSelectedGrowerPanel
                selectedGrower={selectedAggregatorGrower}
              />
            </div>
          ) : null}
          <ResourceBreadcrumb
            ariaLabel="Crop year location"
            items={buildCropYearBreadcrumb({
              cropYear: displayedCropYear,
              cropyearId,
              farm,
              farmId,
              field,
              fieldId,
            })}
          />
          <HistoryViewingBanner
            onOpenHistory={history.openDialog}
            onRestore={history.restoreSelectedVersion}
            onShowCurrent={history.showCurrentVersion}
            onViewNewer={
              history.canViewNewer
                ? () => history.selectAdjacentVersion(-1)
                : undefined
            }
            onViewOlder={
              history.canViewOlder
                ? () => history.selectAdjacentVersion(1)
                : undefined
            }
            restoreDisabled={history.restoreState === "saving"}
            restoring={history.restoreState === "saving"}
            summary={history.activeSummary}
          />
        </div>
        <div className="px-4 pb-6 pt-2 md:px-6 md:pb-8">
          <Stepper
            steps={steps}
            farm={farm}
            setFarm={setFarm}
            field={field}
            cropYear={displayedCropYear}
            setCropYear={updateCropYear}
            fourRAssessment={fourRAssessment}
            setFourRAssessment={updateFourRAssessment}
            fourRState={fourRState}
            setFourRState={updateFourRState}
            activeStep={activeStep}
            hasUnsavedChanges={hasUnsavedChanges}
            commitDraft={autosave.commit}
            onSaveSuccess={({ savedCropYear, savedFourRAssessment, keepDraft }) => {
              setSavedCropYear(cloneFormSnapshot(savedCropYear));
              if (!keepDraft) clearFormDraft(cropYearDraftKey);

              if (!keepDraft) setSavedFourRAssessment(cloneFormSnapshot(savedFourRAssessment || FOUR_R_ASSESSMENT_CREATOR()));
            }}
            onStepChange={handleStepChange}
            readOnly={isViewingHistoricalVersion || !canWriteSelectedGrower}
            summaryContent={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                  Required crop-year inputs
                </p>
                <div className="w-full sm:max-w-[420px]">
                  <CropYearCompletionIndicator cropYear={displayedCropYear} />
                </div>
              </div>
            }
          />
        </div>
      </div>
    </Page>
  );
}
